import {
  HistoryOutlined,
  InboxOutlined,
  LogoutOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Dropdown, Layout, Space, Tag } from 'antd';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { PUBLIC_RUNTIME_PATH_PREFIX } from '@/auth/anonymousRuntime';
import {
  DEV_IDENTITIES,
  type DevIdentity,
  getDevIdentity,
  isDevLoginEnabled,
  setDevIdentity,
} from '@/auth/devLogin';
import { defaultRouteForRole } from '@/auth/RequireRole';
import { useRoles } from '@/auth/useRoles';
import { invalidateSession, useSession } from '@/auth/useSession';
import { getRuntimeConfig, navigableUrl } from '@/config/runtimeConfig';
import type { SupportedLanguage } from '@/i18n';

import { AppFooter } from './AppFooter';
import { BrandMark } from './BrandMark';
import { LanguageSwitcher } from './LanguageSwitcher';
import { SmartFormProvider, useSmartFormPlatform } from './SmartFormContext';
import { SmartFormHeader, type SmartFormHeaderVariant } from './SmartFormHeader';

const { Header, Content } = Layout;

const HEADER_BG = '#0179C1';
const HEADER_HEIGHT = 85;

/**
 * The advisor shell. Split out so the header can READ the platform context that
 * `SmartFormProvider` supplies — a component cannot consume a provider it
 * renders itself.
 */
function SmartFormLayout({
  variant,
  roleLabel,
  headerExtra,
  menuItems,
  name,
  firstName,
  lastName,
  onHome,
  onLogout,
}: {
  variant: SmartFormHeaderVariant;
  roleLabel?: string | null;
  headerExtra?: React.ReactNode;
  menuItems?: MenuProps['items'];
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /** Withheld on the public runtime, which has no home to go to - every destination
   *  leads into AuthGuard and then to a staff login. SmartFormHeader renders the lockup
   *  as a plain image rather than a dead button when this is absent. */
  onHome?: () => void;
  onLogout: () => void;
}) {
  const { t } = useTranslation();
  const platform = useSmartFormPlatform();
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>
      <SmartFormHeader
        variant={variant}
        roleLabel={roleLabel}
        headerExtra={headerExtra}
        menuItems={menuItems}
        platform={platform}
        name={name}
        firstName={firstName}
        lastName={lastName}
        // Validated, not passed through: this value is a deploy-time string that ships
        // EMPTY today, and for an advisor it is the only control in the menu. Anything
        // that is not an absolute http(s) url reads as "not configured".
        dashboardUrl={navigableUrl(getRuntimeConfig().smartFormDashboardUrl)}
        onHome={onHome}
        onLogout={onLogout}
      />
      <Content id="main-content" style={{ padding: 24 }}>
        <Outlet />
      </Content>
      <AppFooter />
    </Layout>
  );
}

export function AppShell() {
  const { t, i18n } = useTranslation();
  // Only the short mark is language-neutral; the reverse lockup this bar falls
  // back to above 1023px is English-only today (see BrandMark).
  const language: SupportedLanguage = i18n.language.startsWith('fr') ? 'fr' : 'en';
  const navigate = useNavigate();
  const location = useLocation();
  const { role, name } = useRoles();
  const { user } = useSession();
  // Gated on DEV IDENTITIES being on, nothing else. There was a second flag for this
  // (`auth.showDevLoginInHeader`) that shipped `false` in every config we write, so the
  // switcher rendered precisely nowhere — a dev affordance that silently did not exist.
  // Whether synthetic identities are in play is the only question that matters here:
  // where they are, this chip is the only way to change or drop one.
  const devIdentity = isDevLoginEnabled() ? getDevIdentity() : null;

  const goHome = () => navigate(role ? defaultRouteForRole(role) : '/');

  const switchIdentity = (next: DevIdentity) => {
    setDevIdentity(next.id);
    // Reset the remembered active-tab for BOTH dashboards so a
    // Dev-Login switch always lands on the natural first tab
    // (Draft for Originator, Published for Approver) rather than
    // whatever tab the previous identity happened to leave open.
    // localStorage is shared across identities on the same browser,
    // so without this a returning identity re-inherits the outgoing
    // one's last view (most annoyingly, the Audit History tab).
    window.localStorage.removeItem('bmo-inflow.originator.activeTab');
    window.localStorage.removeItem('bmo-inflow.approver.activeTab');
    // The /me response is cached module-level for the life of the document,
    // so a client-side navigate leaves every session-driven feature answering
    // as the OUTGOING identity while the header — which reads sessionStorage
    // directly — already shows the new one. That split is how "switched to
    // Frank, form still prefilled Eve's phone and rep code" happened.
    //
    // Drop the cache AND do a full document load: identity is an
    // application-wide axis, and any module holding derived state (roles,
    // session timer, in-flight form values) would otherwise survive the
    // switch. A reload is what signing out and back in does anyway, and this
    // is a Dev-only affordance where being obviously correct beats being
    // smooth.
    invalidateSession();
    window.location.assign(defaultRouteForRole(next.role));
  };

  /**
   * Dev-login identity switcher. Defined ONCE and rendered on every authenticated
   * header — it is the only way to change identity without a real IdP, so losing it
   * on a surface makes that surface untestable. `onDark` is the whole difference:
   * the translucent-white chip that reads well on the blue bar disappears on the
   * white one.
   *
   * Sign out rides in this dropdown rather than in the profile menu, and that
   * placement is the point. An ADVISOR's profile menu has no Sign out in any
   * environment (see SmartFormHeader) — but a synthetic advisor still has to be
   * droppable, or Dev is a one-identity dead end for the advisor runtime. Putting it
   * here keeps the shipping menu byte-identical between Dev and prod while still
   * giving testers the exit: the affordance appears and disappears WITH the dev
   * identities it operates on, and a control labelled "Switch user" that only exists
   * under Dev Login cannot be mistaken for product.
   */
  const renderDevSwitcher = (onDark: boolean) =>
    devIdentity ? (
      <Dropdown
        trigger={['click']}
        placement="bottomRight"
        menu={{
          selectable: true,
          selectedKeys: [devIdentity.id],
          items: [
            ...DEV_IDENTITIES.map((i) => ({
              key: i.id,
              label: (
                <Space>
                  <span>{i.name}</span>
                  <Tag color={i.role === 'Originator' ? 'blue' : 'green'}>{i.role}</Tag>
                </Space>
              ),
              onClick: () => switchIdentity(i),
            })),
            { key: 'dev-signout-sep', type: 'divider' as const },
            {
              key: 'dev-signout',
              icon: <LogoutOutlined />,
              // Straight to /auth/logout, the same route the internal profile menu
              // uses. Under Dev Login that clears the picked identity and lands on
              // the picker; no server call, because there is no BFF session to end.
              label: t('auth.signOut'),
              onClick: () => navigate('/auth/logout'),
            },
          ],
        }}
      >
        <button
          type="button"
          aria-label="switch-identity"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: onDark ? 'rgba(255,255,255,0.15)' : 'transparent',
            color: onDark ? '#fff' : '#1a1a1a',
            border: onDark ? 'none' : '1px solid #d9dde2',
            padding: '4px 10px',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          <SwapOutlined /> Switch user
        </button>
      </Dropdown>
    ) : null;

  const devSwitcher = renderDevSwitcher(false);

  /**
   * Audit history and Archived Workflows. THIS MENU IS THE ONLY WAY INTO EITHER
   * PAGE — nothing else in the app links to them — so a surface that adopts the
   * Smart Form header without receiving these silently strands both. That is
   * what happened to the Originator when it first moved across.
   *
   * Both roles may VIEW the archive; only the Approver gets a Restore action
   * once inside. The audit feed is scoped server-side: an Originator sees only
   * workflows they own, an Approver the firm-wide feed.
   */
  const internalMenuItems: MenuProps['items'] =
    role === 'Approver' || role === 'Originator'
      ? [
          {
            key: 'audit',
            icon: <HistoryOutlined />,
            label: t('auth.auditHistory', { defaultValue: 'Audit history' }),
            onClick: () =>
              navigate(
                role === 'Approver' ? '/approver/audit-history' : '/originator/audit-history',
              ),
          },
          {
            key: 'archive',
            icon: <InboxOutlined />,
            label: t('workflow.archive.title', { defaultValue: 'Archived Workflows' }),
            onClick: () =>
              navigate(role === 'Approver' ? '/approver/archive' : '/originator/archive'),
          },
          { key: 'audit-sep', type: 'divider' as const },
        ]
      : [];

  // Which surfaces get the Smart Form header. Keyed off the ROUTE, not the
  // role, so a dev-login originator previewing an advisor page sees what the
  // advisor sees.
  //
  // Advisor first, then Originator, and now Approver at QA's request so the two
  // internal roles match. The role LABEL still reads "Approver": the Figma frame
  // says "Authenticator", a word this product uses nowhere — not in the code,
  // the Entra group or the routes — so whether BMO renamed the role is still
  // their question to answer. One i18n key changes it if they do.
  const isAdvisorRoute = location.pathname.startsWith('/advisor');
  const isInternalRoute =
    location.pathname.startsWith('/originator') || location.pathname.startsWith('/approver');
  // The public customer runtime. SmartFormHeader already implements this variant end to
  // end - contact block instead of the profile menu, no identity block, no advisor band.
  const isPublicRoute = location.pathname.startsWith(PUBLIC_RUNTIME_PATH_PREFIX);
  // The auth-failure page. It renders inside this shell (App.tsx puts /auth/error in the
  // PUBLIC route group, since a failed sign-in has no session to guard), and lands on the
  // generic header below — so without this the header draws whatever identity /me still
  // answers with, on the very page saying sign-in failed.
  //
  // That is not hypothetical: the BFF deliberately leaves an existing session alone on the
  // rejection paths a forged callback can reach, because clearing it there would hand any
  // third-party page a one-click forced logout (see RejectLoginAsync in AuthEndpoints.cs).
  // So a user who is already signed in and is then refused a NEW login still has a live
  // session when this page renders. Suppress the identity block only — the logo and the
  // language switcher are still wanted here.
  const isAuthErrorRoute = location.pathname.startsWith('/auth/error');

  /**
   * Whether anything actually vouches for an identity right now.
   *
   * Dev Login has no `/me` session by design — AuthGuard skips the check entirely — so there
   * the picked synthetic identity is the authority. Everywhere else it is the server-side
   * session and nothing else.
   *
   * Gating on THIS rather than on `role` alone is what keeps a name off the screen when the
   * session has gone but a `/me` answer cached for the life of the document has not: `role`
   * is derived from that same cached answer, so it outlives the session exactly as the name
   * does and cannot be the thing that authorises showing it. useSession's 401 guard is the
   * other half — it replaces the cached answer the moment the server contradicts it.
   */
  const isAuthenticated = isDevLoginEnabled() ? getDevIdentity() !== null : user !== null;
  /** The identity may be drawn: someone vouches for it, and this page is willing to say so. */
  const showIdentity = isAuthenticated && !isAuthErrorRoute;

  if (isAdvisorRoute || isInternalRoute || isPublicRoute) {
    return (
      <SmartFormProvider>
        <SmartFormLayout
          variant={isPublicRoute ? 'public' : isAdvisorRoute ? 'advisor' : 'internal'}
          roleLabel={isInternalRoute && role ? t(`roles.${role.toLowerCase()}`, role) : null}
          // Advisor routes get it too, and they are the reason it matters: their profile
          // menu has no Sign out at all, so without this a synthetic advisor cannot be
          // dropped. Public is excluded — no session, no identity, nothing to switch.
          headerExtra={isPublicRoute ? null : devSwitcher}
          menuItems={isInternalRoute ? internalMenuItems : undefined}
          // Every identity affordance is withheld on the public runtime. There is no
          // session to name, and the profile menu's Logout would send a customer to a
          // staff logout page. `showIdentity` withholds the same three on any page where
          // nothing vouches for the identity — these routes sit behind AuthGuard, so that
          // is belt and braces, but the header must not be the one surface where a name
          // can outlive its session.
          name={isPublicRoute || !showIdentity ? null : (name ?? user?.name)}
          firstName={isPublicRoute || !showIdentity ? null : user?.firstName}
          lastName={isPublicRoute || !showIdentity ? null : user?.lastName}
          // No link on the advisor or public lockup: the advisor story (GAMXDPT-171 AC-3) removed it —
          // one click left a half-filled form for the dashboard with no warning. Staff pages keep it.
          onHome={isPublicRoute || isAdvisorRoute ? undefined : goHome}
          onLogout={() => navigate('/auth/logout')}
        />
      </SmartFormProvider>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Bypass Blocks (WCAG 2.1 — 2.4.1, Level A). The header and, inside the
          form runtime, a 15-step rail repeat on every page; without this a
          keyboard user tabs through all of it before reaching the content, on
          every single step. Visually hidden until focused. */}
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>
      <Header
        style={{
          height: HEADER_HEIGHT,
          lineHeight: 'normal',
          padding: '0 32px',
          background: HEADER_BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Reverse logo (red roundel + white wordmark) — sits directly on the
            blue header, no backplate, no filter. Swaps to the short reverse
            mark on phone and tablet, same rule as the Smart Form header; the
            28px cap height is set through BrandMark's custom property so the
            two sources share one number instead of an inline height that only
            one of them would fit. */}
        {/* The logo navigates home, so it is a control and must be one: an
            <img onClick> is not focusable and is never announced as actionable
            (WCAG 2.1.1, 4.1.2). Wrapping in a real <button> gives it a tab
            stop, Enter/Space, and a name — and the alt text moves onto the
            button so it isn't announced twice. */}
        <button
          type="button"
          onClick={goHome}
          aria-label={t('a11y.goHome')}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'block',
          }}
        >
          <BrandMark
            tone="reverse"
            language={language}
            alt=""
            style={{ '--bmo-brand-h': '28px' } as React.CSSProperties}
          />
        </button>

        <Space size="middle" align="center">
          {renderDevSwitcher(true)}

          <LanguageSwitcher />

          {showIdentity && role && (
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              menu={{
                items: [
                  // Audit History — opens the Recent Decisions tab on the
                  // caller's own dashboard. The BE endpoint is scoped so
                  // Originators only see the workflows they own; Approvers
                  // see the full firm-wide feed. Same landing behaviour for
                  // both roles: the tab-remembering hook picks up the
                  // localStorage key we prime here on the very next render.
                  ...(role === 'Approver' || role === 'Originator'
                    ? [
                        {
                          key: 'audit',
                          icon: <HistoryOutlined />,
                          label: t('auth.auditHistory', {
                            defaultValue: 'Audit history',
                          }),
                          // Dedicated page (not a dashboard tab) — same
                          // route pattern used elsewhere in the app:
                          // /<role>/audit-history. The page itself
                          // scopes the feed via the BE endpoint, which
                          // filters by owner OID for originators.
                          onClick: () =>
                            navigate(
                              role === 'Approver'
                                ? '/approver/audit-history'
                                : '/originator/audit-history',
                            ),
                        },
                        {
                          // Archive — same route pattern and the same menu
                          // slot as Audit history. Both roles can VIEW it (per
                          // the ticket); only the Approver sees a Restore
                          // action once inside.
                          key: 'archive',
                          icon: <InboxOutlined />,
                          label: t('workflow.archive.title', {
                            defaultValue: 'Archived Workflows',
                          }),
                          onClick: () =>
                            navigate(
                              role === 'Approver' ? '/approver/archive' : '/originator/archive',
                            ),
                        },
                        { key: 'audit-sep', type: 'divider' as const },
                      ]
                    : []),
                  {
                    key: 'signout',
                    icon: <LogoutOutlined />,
                    label: t('auth.signOut'),
                    onClick: () => navigate('/auth/logout'),
                  },
                ],
              }}
            >
              <div className="app-user-trigger" aria-label="user-menu">
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    lineHeight: 1.25,
                  }}
                >
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>
                    {name ?? '—'}
                  </span>
                  {/* Opaque white, not rgba(...,0.9): the 10% alpha composited
                      to #e6f2f9 over the blue header for 4.09:1, under the
                      4.5:1 that 14px text needs (WCAG 1.4.3). */}
                  <span style={{ color: '#fff', fontWeight: 400, fontSize: 14 }}>{role}</span>
                </span>
                <Avatar
                  size={40}
                  shape="square"
                  icon={<UserOutlined />}
                  style={{
                    background: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    borderRadius: 8,
                  }}
                />
              </div>
            </Dropdown>
          )}
        </Space>
      </Header>

      <Content
        style={{
          padding: '24px 32px',
          background: '#f5f6f7',
          // Fill the viewport minus header so the footer is pinned to the
          // bottom even on short pages, and short pages still feel "full".
          minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* `<main>` is the landmark the skip link targets and the one screen
            readers offer as "jump to main content". `tabIndex={-1}` so the
            anchor can actually move focus here — without it the browser scrolls
            but leaves focus behind, and the next Tab returns to the header. */}
        <main
          id="main-content"
          tabIndex={-1}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            outline: 'none',
          }}
        >
          <Outlet />
        </main>
      </Content>
      <AppFooter />
    </Layout>
  );
}
